import React from "react";
import PropTypes from "prop-types";

import Note from "./Note";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

const Section = styled("section")(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.3,
  marginBottom: theme.spacing(0.75),
}));

const SumHeader = styled("span")({
  fontWeight: 600,
});

const SumList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  "& li": {
    fontSize: "0.875rem",
    lineHeight: 1.45,
  },
}));

const BulletList = styled("ul")(({ theme }) => ({
  margin: 0,
  paddingLeft: theme.spacing(2.25),
  "& li": {
    fontSize: "0.875rem",
    lineHeight: 1.5,
    marginBottom: theme.spacing(0.25),
  },
}));

const compactTableSx = {
  "& .MuiTableCell-root": {
    padding: "4px 8px",
    fontSize: "0.8125rem",
    lineHeight: 1.35,
  },
  "& .MuiTableCell-head": {
    fontWeight: 600,
    backgroundColor: (theme) => theme.palette.background.default,
  },
};

const tableContainerSx = {
  maxHeight: 360,
  boxShadow: "none",
  border: (theme) => `1px solid ${theme.palette.divider}`,
};

const noDataSx = { fontSize: "0.875rem", color: "text.secondary" };

const Middot = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0, 0.6),
}));

const banSx = { color: "#c97a00", fontWeight: 500 };

function getBans(result) {
  const bans = [];
  if (result.banned_oic1?.raw === "Yes") bans.push("OIC #1");
  if (result.banned_oic2?.raw === "Yes") bans.push("OIC #2");
  if (result.banned_oic3?.raw === "Yes") bans.push("OIC #3");
  if (result.banned?.raw === "Yes (Live FRT Only)") bans.push("Prohibited in Live FRT");
  return bans;
}

export default function AccordionDetailsContent({ result }) {
  const calibres = result.calibre_shots_barrel_length.raw
    ? result.calibre_shots_barrel_length.raw.map((x) => JSON.parse(x))
    : [];
  const notes = JSON.parse(result.notes.raw);
  const refs = result.cross_references.raw
    ? result.cross_references.raw.map((x) => JSON.parse(x))
    : [];
  const prods = result.also_known_as_product_code.raw || [];
  const years = result.year_dates.raw || [];
  const imports = result.importer.raw || [];

  const bans = getBans(result);
  const headlineCalibres = result.calibres?.raw;

  return (
    <Box sx={{ mt: 1.5, width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography component="div" color="text.primary" sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
          {result.type.raw}<Middot>·</Middot>{result.action.raw}<Middot>·</Middot>{result.legal_classification.raw}
          {bans.map((b, i) => (
            <React.Fragment key={i}>
              <Middot>·</Middot>
              <Box component="span" sx={banSx}>{b}</Box>
            </React.Fragment>
          ))}
        </Typography>
        <Typography component="div" color="text.primary" sx={{ mt: 0.5, fontSize: "0.875rem" }}>
          <Box component="span" sx={{ fontWeight: 600 }}>Calibres:</Box> {headlineCalibres?.length > 0 ? headlineCalibres.join(", ") : "—"}
        </Typography>
      </Box>
      <Section>
        <SectionTitle component="h3">Summary</SectionTitle>
        <SumList>
          <li><SumHeader>FRN:</SumHeader> {result.frn.raw}</li>
          <li><SumHeader>Valid as of:</SumHeader> {result.valid_date.raw}</li>
          <li><SumHeader>Manufacturer:</SumHeader> {result.manufacturer.raw}</li>
          <li><SumHeader>Level:</SumHeader> {result.level.raw}</li>
          <li><SumHeader>Type:</SumHeader> {result.type.raw}</li>
          <li><SumHeader>Action:</SumHeader> {result.action.raw}</li>
          <li><SumHeader>Country:</SumHeader> {result.country_of_manufacturer.raw}</li>
          <li><SumHeader>Serial numbering:</SumHeader> {result.serial_numbering.raw}</li>
          <li><SumHeader>Legal Classification:</SumHeader> {result.legal_classification.raw}</li>
        </SumList>
      </Section>

      <Section>
        <SectionTitle component="h3">Calibre, shots &amp; barrel length</SectionTitle>
        {calibres.length > 0 ? (
          <TableContainer sx={tableContainerSx} component={Paper}>
            <Table stickyHeader size="small" sx={compactTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>FRN</TableCell>
                  <TableCell>Calibre</TableCell>
                  <TableCell>Shots</TableCell>
                  <TableCell>Barrel&nbsp;(mm)</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Legal Authority</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Type Code</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calibres.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.frn}</TableCell>
                    <TableCell>{row.calibre}</TableCell>
                    <TableCell>{row.shots}</TableCell>
                    <TableCell>{row.barrel_mm}</TableCell>
                    <TableCell>{row.legal_classification}</TableCell>
                    <TableCell>{row.legal_authority}</TableCell>
                    <TableCell>{row.level}</TableCell>
                    <TableCell>{row.barrel_type_code}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : <Typography sx={noDataSx}>No data</Typography>}
      </Section>

      <Section>
        <SectionTitle component="h3">Notes</SectionTitle>
        {Object.prototype.hasOwnProperty.call(notes, "make") && <Note heading="Make" value={notes.make} />}
        {Object.prototype.hasOwnProperty.call(notes, "model") && <Note heading="Model" value={notes.model} />}
        {Object.prototype.hasOwnProperty.call(notes, "manufacturer") && <Note heading="Manufacturer" value={notes.manufacturer} />}
        {Object.prototype.hasOwnProperty.call(notes, "type") && <Note heading="Type" value={notes.type} />}
        {Object.prototype.hasOwnProperty.call(notes, "action") && <Note heading="Action" value={notes.action} />}
        {Object.prototype.hasOwnProperty.call(notes, "calibre") && <Note heading="Calibre" value={notes.calibre} />}
        {Object.prototype.hasOwnProperty.call(notes, "shots") && <Note heading="Shots" value={notes.shots} />}
        {Object.prototype.hasOwnProperty.call(notes, "barrel") && <Note heading="Barrel" value={notes.barrel} />}
        {Object.prototype.hasOwnProperty.call(notes, "serial_number") && <Note heading="Serial Number" value={notes.serial_number} />}
        {Object.prototype.hasOwnProperty.call(notes, "canadian_law_comments") && <Note heading="Canadian Law Comments" value={notes.canadian_law_comments} />}
        {Object.prototype.hasOwnProperty.call(notes, "remarks") && <Note heading="Remarks" value={notes.remarks} />}
        {Object.prototype.hasOwnProperty.call(notes, "other_markings") && <Note heading="Other Markings" value={notes.other_markings} />}
      </Section>

      <Section>
        <SectionTitle component="h3">Cross-references</SectionTitle>
        {refs.length > 0 ? (
          <TableContainer sx={tableContainerSx} component={Paper}>
            <Table stickyHeader size="small" sx={compactTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>FRN</TableCell>
                  <TableCell>Make</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refs.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.frn}</TableCell>
                    <TableCell>{row.make}</TableCell>
                    <TableCell>{row.model}</TableCell>
                    <TableCell>{row.manufacturer}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.action}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : <Typography sx={noDataSx}>No data</Typography>}
      </Section>

      {prods.length > 0 && (
        <Section>
          <SectionTitle component="h3">Also known as / product code</SectionTitle>
          <BulletList>{prods.map((v, i) => <li key={i}>{v}</li>)}</BulletList>
        </Section>
      )}
      {years.length > 0 && (
        <Section>
          <SectionTitle component="h3">Year dates</SectionTitle>
          <BulletList>{years.map((v, i) => <li key={i}>{v}</li>)}</BulletList>
        </Section>
      )}
      {imports.length > 0 && (
        <Section>
          <SectionTitle component="h3">Importer</SectionTitle>
          <BulletList>{imports.map((v, i) => <li key={i}>{v}</li>)}</BulletList>
        </Section>
      )}
    </Box>
  );
}

AccordionDetailsContent.propTypes = {
  result: PropTypes.object,
};
